import React, { useEffect, useState } from 'react';
import DraggableFab from './DraggableFab';

const valueText = (value, digits = 2) => {
  if (value == null || value === '') return '—';
  return Number(value).toLocaleString('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const quantityText = (value) => {
  if (value == null || value === '') return '—';
  return Number(value).toLocaleString('en-GB', { maximumFractionDigits: 4 });
};

// Compact enough for a narrow column; the full ISO date stays in the tooltip
// so the exact day is one hover away.
const dateText = (value) => {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// What this position loses if the stop is hit, measured from the entry. A
// stop above entry is locked-in profit rather than a loss, so it comes back
// negative and is reported as such instead of being counted as risk.
const riskAtStop = (order) => {
  const entry = Number(order.entry_price);
  const stop = Number(order.stop_price);
  const quantity = Number(order.quantity);
  if (!(entry > 0) || !(stop > 0) || !(quantity > 0)) return null;
  return (entry - stop) * quantity;
};

const blindSymbolText = (value) => {
  const symbol = String(value || '');
  if (!symbol) return '';
  if (symbol.length === 1) return `${symbol}...`;
  return `${symbol[0]}...${symbol[symbol.length - 1]}`;
};

/* Fully-closed trades linger in Holdings struck through so the chart stays
   reviewable; "Release" drops them from the list. The released flag lives on
   the trade in Postgres (orders.released_at), not in localStorage: it used to
   be browser-local, so a trade released on one device reappeared on every
   other one - the same account on a phone would still be shown trades that
   had already been reviewed and released on the desktop. */

const ClosedHoldingRow = ({ order, showSymbols, onSelect, onRelease, pctUp }) => {
  const realised = order.exit_price != null
    ? (Number(order.exit_price) - Number(order.entry_price)) * Number(order.quantity)
    : null;
  const realisedPct = realised != null && Number(order.entry_price) > 0
    ? ((Number(order.exit_price) - Number(order.entry_price)) / Number(order.entry_price)) * 100
    : null;
  const dayMove = pctUp != null ? Number(pctUp) : null;
  return (
    <tr className="order-cancelled closed-holding" onClick={() => onSelect(order)}>
      <td className="symbol">
        {showSymbols ? order.symbol : blindSymbolText(order.symbol)}
        <small>{order.market}</small>
        {order.ibkr_account && (
          <small className="ibkr-account" title={`IBKR account ${order.ibkr_account}`}>
            {order.ibkr_account}
          </small>
        )}
        {order.exit_reason && (
          <span className="status-pill" title="Exit reason">{String(order.exit_reason).replaceAll('_', ' ')}</span>
        )}
        {order.journal_note && <span className="status-pill" title={order.journal_note}>notes</span>}
      </td>
      <td className={`number ${dayMove != null ? (dayMove >= 0 ? 'positive' : 'negative') : ''}`} title="Day % move">
        {dayMove != null ? `${valueText(dayMove)}%` : '—'}
      </td>
      <td className={`number stacked ${(realised || 0) >= 0 ? 'positive' : 'negative'}`}>
        {realised != null ? (
          <><span>{valueText(realised)}</span><small>{valueText(realisedPct)}%</small></>
        ) : '—'}
      </td>
      <td className="number holding-quantity">{quantityText(order.quantity)}</td>
      <td className="number stacked">
        <span>{valueText(order.entry_price, 2)}</span>
        <small title={order.entry_date || 'No entry date'}>{dateText(order.entry_date)}</small>
      </td>
      <td className="number stacked">
        <span>{valueText(order.exit_price, 2)}</span>
        <small title={`Exited ${order.exit_date || ''}`}>{dateText(order.exit_date)}</small>
      </td>
      <td className="holding-actions" onClick={(event) => event.stopPropagation()}>
        {order.partial_close ? (
          <span className="status-pill" title="Partial exits are shown on the chart and are not added to the journal">partial exit</span>
        ) : (
          <button type="button" title="Add a journal note and remove this trade from the list" onClick={() => onRelease(order)}>
            Release
          </button>
        )}
      </td>
    </tr>
  );
};

const HoldingRow = ({ order, onSelect, onClose, onCancel, onUpdateStop, onFill, busy, showSymbols, pickTarget, onRequestPick, stopPickEnabled, pctUp }) => {
  const dayMove = pctUp != null ? Number(pctUp) : null;
  const risk = riskAtStop(order);
  const [stopPrice, setStopPrice] = useState(String(order.stop_price ?? ''));
  const [exitQuantity, setExitQuantity] = useState(String(order.quantity ?? ''));

  useEffect(() => {
    setStopPrice(String(order.stop_price ?? ''));
  }, [order.stop_price]);

  useEffect(() => {
    setExitQuantity(String(order.quantity ?? ''));
  }, [order.quantity]);

  return (
    <tr onClick={() => onSelect(order)}>
      <td className="symbol">
        {showSymbols ? order.symbol : blindSymbolText(order.symbol)}
        <small>{order.market}</small>
        {/* Holdings come from more than one IBKR account, so the row has to
            say which one it sits in. Absent for non-IBKR trades. */}
        {order.ibkr_account && (
          <small className="ibkr-account" title={`IBKR account ${order.ibkr_account}`}>
            {order.ibkr_account}
          </small>
        )}
        {order.stop_hit_date && (
          <span className="hit-pills"><span className="status-pill sl-hit">SL</span></span>
        )}
      </td>
      <td className={`number ${dayMove != null ? (dayMove >= 0 ? 'positive' : 'negative') : ''}`} title="Day % move">
        {dayMove != null ? `${valueText(dayMove)}%` : '—'}
      </td>
      <td className={`number stacked ${(order.unrealised_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
        {order.status === 'open' ? (
          <>
            <span>{valueText(order.unrealised_pnl)}</span>
            <small>{valueText(order.unrealised_pct)}%</small>
          </>
        ) : '—'}
      </td>
      <td className="number holding-quantity">
        {quantityText(order.quantity)}
        {order.partial_closed_pct != null && (
          <small className="partial-pct" title={`${Math.round(order.partial_closed_pct)}% of the position has been closed`}>
            {' '}{Math.round(order.partial_closed_pct)}% closed
          </small>
        )}
      </td>
      <td className="number stacked">
        <span>{valueText(order.entry_price, 2)}</span>
        <small title={order.entry_date || 'No entry date'}>{dateText(order.entry_date)}</small>
      </td>
      <td className="stop-editor" onClick={(event) => event.stopPropagation()}>
        <div className="holding-control-row">
          <input
            type="number"
            min="0.0001"
            step="0.0001"
            aria-label={`Stop price for ${order.symbol}`}
            value={stopPrice}
            onChange={(event) => setStopPrice(event.target.value)}
          />
          <button type="button" disabled={busy || !(Number(stopPrice) > 0)} onClick={() => onUpdateStop(order.id, stopPrice)}>
            Upd
          </button>
        </div>
        <small className="stop-risk" title={risk == null
          ? 'No stop set, so the downside is not capped'
          : (risk > 0
            ? `Loses ${valueText(risk)} if the stop is hit`
            : `Stop is above entry: locks in ${valueText(Math.abs(risk))}`)}>
          {risk == null
            ? <span className="risk-unset">no stop</span>
            : (risk > 0
              ? <span className="negative">-{valueText(risk)}</span>
              : <span className="positive">+{valueText(Math.abs(risk))}</span>)}
        </small>
      </td>
      <td className="holding-actions" onClick={(event) => event.stopPropagation()}>
        {order.status === 'open' ? (
          <div className="holding-control-row">
            <input
              type="number"
              min="0.0001"
              max={order.quantity}
              step={order.market === 'US' ? '0.0001' : '1'}
              aria-label={`Close quantity for ${order.symbol}`}
              title="Quantity to close"
              value={exitQuantity}
              onChange={(event) => setExitQuantity(event.target.value)}
            />
            <button
              type="button"
              disabled={busy || !(Number(exitQuantity) > 0) || Number(exitQuantity) > Number(order.quantity)}
              onClick={() => onClose(order.id, 'manual_close', exitQuantity)}
            >
              Close
            </button>
          </div>
        ) : (
          <div className="holding-control-row">
            <button
              type="button"
              disabled={busy}
              title={`Mark filled at the limit price${order.stop_price ? ` with stop ${order.stop_price}` : ''}`}
              onClick={() => onFill(order.id)}
            >
              Filled
            </button>
            <button type="button" disabled={busy} onClick={() => onCancel(order.id)}>Cancel</button>
          </div>
        )}
      </td>
    </tr>
  );
};

const SortableTh = ({ label, sortKey, sort, onSort, number }) => (
  <th className={number ? 'number' : ''}>
    <button type="button" className="th-sort" onClick={() => onSort(sortKey)}>
      {label}{sort.key === sortKey ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  </th>
);

// A stacked column sorts by either of its two lines, so the merged headers
// keep the sorts the separate columns used to have.
const DualSortTh = ({ top, topKey, bottom, bottomKey, sort, onSort, number }) => (
  <th className={number ? 'number' : ''}>
    <button type="button" className="th-sort" onClick={() => onSort(topKey)}>
      {top}{sort.key === topKey ? (sort.dir === 'asc' ? ' \u2191' : ' \u2193') : ''}
    </button>
    <span className="th-sort-sep">/</span>
    <button type="button" className="th-sort" onClick={() => onSort(bottomKey)}>
      {bottom}{sort.key === bottomKey ? (sort.dir === 'asc' ? ' \u2191' : ' \u2193') : ''}
    </button>
  </th>
);

const PendingRow = ({
  order, cancelled, showSymbols, busy, qtyEditable,
  onSelect, onUpdateEntry, onUpdateStop, onUpdateQty, onFill, onCancel,
  pickTarget, onRequestPick,
}) => {
  const [entryDraft, setEntryDraft] = useState(String(order.limit_price ?? ''));
  const [stopDraft, setStopDraft] = useState(String(order.stop_price ?? ''));
  const [qtyDraft, setQtyDraft] = useState(String(order.quantity ?? ''));

  useEffect(() => {
    setEntryDraft(String(order.limit_price ?? ''));
  }, [order.limit_price]);

  useEffect(() => {
    setStopDraft(String(order.stop_price ?? ''));
  }, [order.stop_price]);

  useEffect(() => {
    setQtyDraft(String(order.quantity ?? ''));
  }, [order.quantity]);

  return (
    <tr
      className={`${cancelled ? 'order-cancelled' : ''} ${order.symbol === undefined ? '' : ''}`}
      onClick={() => !cancelled && onSelect(order)}
    >
      <td className="symbol">
        {showSymbols ? order.symbol : blindSymbolText(order.symbol)}
        <small>{order.market}</small>
      </td>
      <td className="number">{order.placed_date || ''}</td>
      <td className="number holding-quantity" onClick={(event) => event.stopPropagation()}>
        {cancelled || !qtyEditable ? (
          quantityText(order.quantity)
        ) : (
          <div className="holding-control-row">
            <input
              type="number"
              min="1"
              step="1"
              aria-label={`Quantity for ${order.symbol}`}
              value={qtyDraft}
              onChange={(event) => setQtyDraft(event.target.value)}
            />
            <button
              type="button"
              disabled={busy || !(Math.floor(Number(qtyDraft)) > 0) || Math.floor(Number(qtyDraft)) === Number(order.quantity)}
              onClick={() => onUpdateQty(order.id, qtyDraft)}
            >
              Upd
            </button>
          </div>
        )}
      </td>
      <td className="stop-editor" onClick={(event) => event.stopPropagation()}>
        {cancelled ? (
          <span>{valueText(order.limit_price, 2)}</span>
        ) : (
          <div className="holding-control-row">
            <input
              type="number"
              min="0.0001"
              step="0.0001"
              aria-label={`Entry price for ${order.symbol}`}
              placeholder="Entry"
              value={entryDraft}
              onChange={(event) => setEntryDraft(event.target.value)}
            />
            <button
              type="button"
              disabled={busy || !(Number(entryDraft) > 0)}
              onClick={() => onUpdateEntry(order.id, entryDraft)}
            >
              Upd
            </button>
          </div>
        )}
      </td>
      <td className="stop-editor" onClick={(event) => event.stopPropagation()}>
        {cancelled ? (
          <span>{order.stop_price ? valueText(order.stop_price, 2) : '—'}</span>
        ) : (
          <div className="holding-control-row">
            <input
              type="number"
              min="0.0001"
              step="0.0001"
              aria-label={`Stop loss for ${order.symbol}`}
              placeholder="Stop"
              value={stopDraft}
              onChange={(event) => setStopDraft(event.target.value)}
            />
            <button type="button" disabled={busy} onClick={() => onUpdateStop(order.id, stopDraft)}>
              Upd
            </button>
          </div>
        )}
      </td>
      <td className="holding-actions" onClick={(event) => event.stopPropagation()}>
        {cancelled ? (
          <span className="status-pill cancelled">cancelled</span>
        ) : (
          <div className="holding-control-row">
            <button
              type="button"
              disabled={busy || !(Number(entryDraft) > 0)}
              title={Number(entryDraft) > 0
                ? `Mark filled at ${entryDraft} and move to holdings today`
                : 'Enter the entry price first'}
              onClick={() => onFill(order.id, entryDraft)}
            >
              Filled
            </button>
            <button type="button" disabled={busy} onClick={() => onCancel(order.id)}>Cancel</button>
          </div>
        )}
      </td>
    </tr>
  );
};

const HOLDINGS_SORTS = {
  symbol: (o) => o.symbol || '',
  // entry_date is ISO, so string order is date order. A holding with no entry
  // date sorts to the end either way rather than jumping to the top.
  entry_date: (o) => o.entry_date || '9999-99-99',
  quantity: (o) => Number(o.quantity) || 0,
  unrealised_pnl: (o) => Number(o.unrealised_pnl) || 0,
  unrealised_pct: (o) => Number(o.unrealised_pct) || 0,
  entry_price: (o) => Number(o.entry_price) || 0,
  // A position with no stop has unbounded risk, so it sorts above every
  // measured one rather than sinking to the bottom as a zero.
  risk: (o) => (riskAtStop(o) == null ? Infinity : riskAtStop(o)),
  stop_price: (o) => (o.stop_price == null ? -Infinity : Number(o.stop_price)),
};

const ORDERS_SORTS = {
  symbol: (o) => o.symbol || '',
  placed_date: (o) => o.placed_date || '',
  quantity: (o) => Number(o.quantity) || 0,
  limit_price: (o) => Number(o.limit_price) || 0,
  stop_price: (o) => (o.stop_price == null ? -Infinity : Number(o.stop_price)),
};

const makeSortHandler = (sort, setSort) => (key) => setSort((current) => ({
  key,
  dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc',
}));

const sortRows = (rows, sorts, sort) => [...rows].sort((a, b) => {
  // Cancelled orders always sink to the bottom.
  if (Boolean(a.cancelledFlag) !== Boolean(b.cancelledFlag)) return a.cancelledFlag ? 1 : -1;
  const get = sorts[sort.key] || ((o) => o.id);
  const av = get(a);
  const bv = get(b);
  const comparison = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
  return sort.dir === 'asc' ? comparison : -comparison;
});

const HoldingsTable = ({
  showSymbols = false,
  orders, onSelect, onClose, onCancel, onUpdateStop, onUpdateEntry, onUpdateQty, onFill, busy,
  pickTarget, onRequestPick, entryEditable = true, stopPickEnabled = true, qtyEditable = false,
  retainClosed = false,
  onReleaseTrade,
  onPrevious, onNext, canPrevious, canNext, navPosition, navTotal,
  pctUpBySymbol = {},
}) => {
  const [tab, setTab] = useState('holdings');
  const [sort, setSort] = useState({ key: 'unrealised_pct', dir: 'desc' });
  const holdings = orders.filter((order) => order.status === 'open');
  // Training only: fully-closed trades stay visible (struck through) until
  // explicitly released, so the chart can be reviewed after exit. Partial
  // exits are excluded - there is nothing to review on them, and keeping
  // them out leaves the symbol free to be autofilled by a later partial.
  const heldClosed = retainClosed
    ? orders.filter((order) => (
      order.status === 'closed'
        && !order.partial_close
        && !order.released_at
    ))
    : [];
  // Release flow: optionally jot a journal note before the trade leaves the
  // list. Note and release go in one request so a released trade can never
  // end up without the note that was written with it.
  const [noteDraft, setNoteDraft] = useState(null);
  const [releaseError, setReleaseError] = useState('');
  const confirmRelease = async () => {
    const target = noteDraft;
    if (!target) return;
    try {
      await onReleaseTrade(target.order.id, target.text.trim());
      setNoteDraft(null);
      setReleaseError('');
    } catch (error) {
      // Keep the dialog open with the text intact - silently closing it would
      // look like the release worked when the trade is still on every device.
      setReleaseError(error?.message || 'Unable to release the trade');
    }
  };
  // Worst case across the book. Only positive risks are summed: a stop above
  // entry protects a gain and must not be allowed to offset real downside on
  // another position. Unstopped positions are counted separately, because a
  // total that quietly ignores them reads as safer than the book actually is.
  const openHoldings = orders.filter((order) => order.status === 'open');
  const totalRisk = openHoldings.reduce((sum, order) => {
    const risk = riskAtStop(order);
    return sum + (risk != null && risk > 0 ? risk : 0);
  }, 0);
  const unstoppedCount = openHoldings.filter((order) => riskAtStop(order) == null).length;

  const pendingOrders = orders.filter((order) => order.status === 'pending');
  let rows;
  if (tab === 'orders') {
    // Cancelling an order withdraws it: there is nothing left to act on and
    // no fill to review, so it leaves the list rather than lingering struck
    // through. That treatment is for closed trades, which do have a result.
    rows = sortRows(pendingOrders, ORDERS_SORTS, sort);
  } else {
    const get = sort.key === 'pct'
      ? ((o) => Number(pctUpBySymbol[o.symbol] ?? 0))
      : (HOLDINGS_SORTS[sort.key] || ((o) => o.id));
    rows = [...holdings, ...heldClosed].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      const comparison = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sort.dir === 'asc' ? comparison : -comparison;
    });
  }
  return (
    <section className={`holdings-section${stopPickEnabled ? '' : ' no-stop-pick'}`}>
      <div className="section-heading">
        <div className="holdings-tabs" role="tablist" aria-label="Holdings and orders">
          {[
            ['holdings', 'Holdings', holdings.length + heldClosed.length],
            ['orders', 'Pending orders', pendingOrders.length],
          ].map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={tab === value ? 'active' : ''}
              onClick={() => setTab(value)}
            >
              {label} ({count})
            </button>
          ))}
        </div>
        {tab === 'holdings' && openHoldings.length > 0 && (
          <span className="holdings-risk-total"
            title={unstoppedCount
              ? `Worst case if every stop is hit. ${unstoppedCount} position${unstoppedCount === 1 ? ' has' : 's have'} no stop and is not included.`
              : 'Worst case if every stop is hit'}>
            <small>Risk</small>
            <strong className="negative">-{valueText(totalRisk)}</strong>
            {unstoppedCount > 0 && (
              <em className="risk-unset">+{unstoppedCount} unstopped</em>
            )}
          </span>
        )}
        {onPrevious && (
          <div className="chart-prev-next" aria-label="Screener result navigation">
            <button type="button" onClick={onPrevious} disabled={!canPrevious} title="Previous screener result">‹</button>
            <span>{navPosition}/{navTotal}</span>
            <button type="button" onClick={onNext} disabled={!canNext} title="Next screener result">›</button>
            <div className="holdings-nav-desktop">
              <button type="button" onClick={onPrevious} disabled={!canPrevious} title="Previous screener result">Prev</button>
              <button type="button" onClick={onNext} disabled={!canNext} title="Next screener result">Next</button>
            </div>
          </div>
        )}
        {onNext && (
          <DraggableFab label="Prev" className="floating-prev-button" storageKey="screener2-prev-fab-pos" onAction={onPrevious} disabled={!canPrevious} />
        )}
        {onNext && (
          <DraggableFab label="Next" className="floating-next-button" storageKey="screener2-next-fab-pos" onAction={onNext} disabled={!canNext} />
        )}
      </div>
      {(rows.length === 0 ? (
        <div className="holdings-empty">{tab === 'orders' ? 'No pending orders' : 'No open holdings'}</div>
      ) : (
        <div className="holdings-scroll">
          <table className="holdings-table">
            <thead>
              {tab === 'orders' ? (
                <tr>
                  <SortableTh label="Ticker" sortKey="symbol" sort={sort} onSort={makeSortHandler(sort, setSort)} />
                  <SortableTh label="Placed" sortKey="placed_date" sort={sort} onSort={makeSortHandler(sort, setSort)} />
                  <SortableTh label="Qty" sortKey="quantity" sort={sort} onSort={makeSortHandler(sort, setSort)} number />
                  <th className="number">Entry</th>
                  <th className="number">Stop</th>
                  <th>Fill / Cancel</th>
                </tr>
              ) : (
                <tr>
                  <SortableTh label="Ticker" sortKey="symbol" sort={sort} onSort={makeSortHandler(sort, setSort)} />
                  <SortableTh label="Day" sortKey="pct" sort={sort} onSort={makeSortHandler(sort, setSort)} number />
                  <DualSortTh top="P/L" topKey="unrealised_pnl" bottom="%" bottomKey="unrealised_pct" sort={sort} onSort={makeSortHandler(sort, setSort)} number />
                  <SortableTh label="Qty" sortKey="quantity" sort={sort} onSort={makeSortHandler(sort, setSort)} number />
                  <DualSortTh top="E" topKey="entry_price" bottom="Dt" bottomKey="entry_date" sort={sort} onSort={makeSortHandler(sort, setSort)} number />
                  <SortableTh label="Stop / risk" sortKey="stop_price" sort={sort} onSort={makeSortHandler(sort, setSort)} number />
                  <th>Close</th>
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((order) => (
                order.status === 'closed' ? (
                  <ClosedHoldingRow
                    key={order.id}
                    order={order}
                    showSymbols={showSymbols}
                    pctUp={pctUpBySymbol[order.symbol]}
                    onSelect={onSelect}
                    onRelease={(closedOrder) => setNoteDraft({ order: closedOrder, text: closedOrder.journal_note || '' })}
                  />
                ) : order.cancelledFlag || order.status === 'cancelled' ? (
                  <PendingRow
                    key={order.id}
                    cancelled
                    showSymbols={showSymbols}
                    order={order}
                    onSelect={onSelect}
                    onUpdateEntry={() => {}}
                    onUpdateStop={() => {}}
                    onUpdateQty={() => {}}
                    onFill={onFill}
                    onCancel={onCancel}
                    pickTarget={pickTarget}
                    onRequestPick={onRequestPick}
                    busy={busy}
                  />
                ) : tab === 'orders' ? (
                  <PendingRow
                    key={order.id}
                    showSymbols={showSymbols}
                    order={order}
                    onSelect={onSelect}
                    onUpdateEntry={onUpdateEntry}
                    onUpdateStop={onUpdateStop}
                    onUpdateQty={onUpdateQty}
                    onFill={onFill}
                    onCancel={onCancel}
                    pickTarget={pickTarget}
                    onRequestPick={onRequestPick}
                    busy={busy}
                  />
                ) : (
                  <HoldingRow
                    key={order.id}
                    showSymbols={showSymbols}
                    order={order}
                    pctUp={pctUpBySymbol[order.symbol]}
                    onSelect={onSelect}
                    onClose={onClose}
                    onCancel={onCancel}
                    onUpdateStop={onUpdateStop}
                    onFill={onFill}
                    busy={busy}
                    pickTarget={pickTarget}
                    onRequestPick={onRequestPick}
                    stopPickEnabled={stopPickEnabled}
                  />
                )
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {noteDraft && (
        <div className="admin-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) { setNoteDraft(null); setReleaseError(''); }
        }}>
          <div className="journal-note-dialog" role="dialog" aria-modal="true" aria-label="Trading journal note">
            <header>
              <strong>Journal — {noteDraft.order.symbol}</strong>
              <button type="button" onClick={() => { setNoteDraft(null); setReleaseError(''); }} aria-label="Close">×</button>
            </header>
            <textarea
              value={noteDraft.text}
              onChange={(event) => setNoteDraft((current) => ({ ...current, text: event.target.value }))}
              placeholder="What did you learn? Why did it work or not?"
              rows={5}
              autoFocus
            />
            {releaseError && <p className="journal-note-error" role="alert">{releaseError}</p>}
            <div className="journal-note-actions">
              <button type="button" onClick={() => { setNoteDraft(null); setReleaseError(''); }}>Cancel</button>
              <button type="button" className="primary" onClick={confirmRelease}>
                Save note &amp; release
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HoldingsTable;
